import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * PageTitle Component
 * 
 * A unified component for all page titles and headings across the application.
 * Provides consistent styling and behavior for different heading levels following
 * the 80/20 principle - 80% consistent behavior, 20% configurable variations.
 * 
 * This component handles semantic HTML structure, accessibility, responsive design,
 * and special formatting requirements including Hebrew text with special characters.
 * 
 * @example
 * // Basic usage
 * <PageTitle variant="page">My Page Title</PageTitle>
 * 
 * @example
 * // Hero title with beta label
 * <PageTitle variant="hero" showBeta center>
 *   Welcome to Our App
 * </PageTitle>
 * 
 * @example
 * // Logo with click handler
 * <PageTitle 
 *   variant="logo" 
 *   onClick={() => navigate('/')}
 *   ariaLabel="Go to homepage"
 * >
 *   בואתִמצא
 * </PageTitle>
 * 
 * @example
 * // Custom styling and accessibility
 * <PageTitle
 *   variant="section"
 *   level="h4"
 *   color="text-blue-600"
 *   className="border-b-2"
 *   id="section-heading"
 *   ariaLabel="Section: User Settings"
 * >
 *   User Settings
 * </PageTitle>
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The title text content
 * @param {('hero'|'page'|'section'|'subsection'|'logo')} [props.variant='page'] - Visual and semantic variant
 * @param {('h1'|'h2'|'h3'|'h4'|'h5'|'h6')} [props.level] - HTML heading level, auto-determined by variant if not specified
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Additional inline styles
 * @param {boolean} [props.center=false] - Whether to center align the title
 * @param {string} [props.color] - Text color override (Tailwind classes)
 * @param {boolean} [props.showBeta=false] - Whether to show beta label (for hero/logo variants)
 * @param {string} [props.ariaLabel] - Accessibility label for screen readers
 * @param {Function} [props.onClick] - Click event handler (typically for logo variant)
 * @param {string} [props.id] - Element ID for linking and accessibility
 * @returns {React.Element} The rendered title component
 */
const PageTitle = ({
  // Required props first
  children,
  
  // Core configuration props
  variant = 'page',
  level,
  
  // Optional styling props with defaults
  className = '',
  style = {},
  center = false,
  color,
  
  // Feature flags
  showBeta = false,
  
  // Accessibility props
  ariaLabel,
  id,
  
  // Event handlers
  onClick,
  
  // Rest props for flexibility
  ...restProps
}) => {
  // Memoized heading level determination
  const headingLevel = useMemo(() => {
    if (level) return level;
    
    const variantToLevel = {
      hero: 'h1',
      page: 'h1',
      logo: 'h1',
      section: 'h2',
      subsection: 'h3'
    };
    
    return variantToLevel[variant] || 'h1';
  }, [level, variant]);

  // Memoized variant styles configuration
  const variantStyles = useMemo(() => {
    const styleConfig = {
      hero: 'text-4xl md:text-5xl font-bold mb-4 relative',
      page: 'text-3xl font-bold mb-6',
      section: 'text-2xl font-semibold mb-4',
      subsection: 'text-xl font-semibold mb-3',
      logo: 'text-2xl font-bold cursor-pointer'
    };

    return styleConfig[variant] || styleConfig.page;
  }, [variant]);

  // Memoized center alignment styles
  const centerStyles = useMemo(() => {
    if (!center) return '';
    
    return variant === 'logo' 
      ? 'absolute left-1/2 transform -translate-x-1/2' 
      : 'text-center';
  }, [center, variant]);

  // Memoized color styles
  const colorStyles = useMemo(() => {
    if (color) return color;
    
    // Default colors for specific variants
    const defaultColors = {
      hero: 'text-gray-800',
      page: 'text-gray-800',
      subsection: 'text-gray-700',
      logo: 'text-gray-800'
    };
    
    return defaultColors[variant] || '';
  }, [color, variant]);

  // Memoized combined className
  const combinedClassName = useMemo(() => {
    return [
      'page-title', // Base class for styling and testing
      `page-title-${variant}`, // Variant-specific class
      variantStyles,
      centerStyles,
      colorStyles,
      className // User-provided classes last for override capability
    ].filter(Boolean).join(' ');
  }, [variantStyles, centerStyles, colorStyles, className, variant]);

  // Memoized special content renderer for Hebrew and beta labels
  const specialContent = useMemo(() => {
    // Handle Hebrew special character formatting
    if ((variant === 'hero' || variant === 'logo') && 
        typeof children === 'string' && 
        children.includes('בואתִמצא')) {
      
      const parts = children.split('תִ');
      if (parts.length === 2) {
        const specialCharSize = variant === 'hero' 
          ? 'text-7xl md:text-8xl' 
          : 'text-4xl md:text-4xl';
        
        return (
          <>
            {parts[0]}
            <span className={specialCharSize} aria-hidden="true">תִ</span>
            {parts[1]}
            {showBeta && (
              <span 
                className="text-xs text-gray-500 absolute bottom-0"
                aria-label="Beta version"
              >
                Beta
              </span>
            )}
          </>
        );
      }
    }
    
    // Regular content rendering
    return (
      <>
        {children}
        {showBeta && (
          <span 
            className={
              variant === 'hero' || variant === 'logo' 
                ? "text-xs text-gray-500 absolute bottom-0" 
                : "text-xs text-gray-500 ml-2"
            }
            aria-label="Beta version"
          >
            Beta
          </span>
        )}
      </>
    );
  }, [children, variant, showBeta]);

  // Memoized click handler for performance
  const handleClick = useCallback((event) => {
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);

  // Create the heading element
  const HeadingElement = headingLevel;

  // Error boundary fallback for invalid children
  if (children === undefined) {
    console.warn('PageTitle: children prop is required');
    return null;
  }
  // For empty string or null, still render the heading element for semantic structure

  return (
    <HeadingElement
      id={id}
      className={combinedClassName}
      style={style}
      aria-label={ariaLabel}
      onClick={onClick ? handleClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      } : undefined}
      {...restProps}
    >
      {specialContent}
    </HeadingElement>
  );
};

// Comprehensive PropTypes validation
PageTitle.propTypes = {
  // Required props
  children: PropTypes.node.isRequired,
  
  // Core configuration
  variant: PropTypes.oneOf(['hero', 'page', 'section', 'subsection', 'logo']),
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  
  // Styling props
  className: PropTypes.string,
  style: PropTypes.object,
  center: PropTypes.bool,
  color: PropTypes.string,
  
  // Feature flags
  showBeta: PropTypes.bool,
  
  // Accessibility props
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
  
  // Event handlers
  onClick: PropTypes.func
};

// Default props for documentation and consistency
PageTitle.defaultProps = {
  variant: 'page',
  className: '',
  style: {},
  center: false,
  showBeta: false
};

export default PageTitle; 